import type mapboxgl from 'mapbox-gl';

export type ExtrusionLightSettings = {
  /** Direction in local mercator space (x east, y south, z up). */
  dir: readonly [number, number, number];
  /** Minimum light floor; keeps extrusions from going grey/black. */
  ambient: number;
  /** How strongly to apply diffuse shading (0 = flat, 1 = full). */
  strength: number;
};

type StyleLight = {
  position?: readonly [number, number, number];
  intensity?: number;
};

/**
 * Resolve light settings from the active Mapbox style (when available).
 * Falls back to a stable default when style light is unavailable.
 */
export function resolveExtrusionLightSettings(
  map: mapboxgl.Map,
): ExtrusionLightSettings {
  const styleLight = getStyleLight(map);

  const intensity = clamp01(styleLight?.intensity ?? 0.5);

  // Henrik: keep extrusion color readable; shading is subtle (think Boverket diagram, not game rendering).
  const ambient = clamp(0.9 + intensity * 0.05, 0.9, 0.97);
  const strength = clamp(0.08 + intensity * 0.12, 0.06, 0.2);

  const dir = styleLight?.position
    ? lightPositionToDirection(styleLight.position)
    : (normalize3([0.4, 0.6, 0.7]) as [number, number, number]);

  return { dir, ambient, strength };
}

function getStyleLight(map: mapboxgl.Map): StyleLight | undefined {
  const api = map as unknown as {
    getLights?: () => unknown;
    getLight?: () => unknown;
  };

  // Prefer the non-deprecated API when available. If it returns nothing, we still fall back to defaults.
  const candidate = api.getLights
    ? pickLightCandidate(api.getLights())
    : api.getLight?.();
  if (!candidate || typeof candidate !== 'object') return undefined;

  const light = candidate as {
    position?: unknown;
    intensity?: unknown;
  };

  const position = isLightPosition(light.position) ? light.position : undefined;
  const intensity =
    typeof light.intensity === 'number' ? light.intensity : undefined;

  return { position, intensity };
}

function pickLightCandidate(value: unknown): unknown {
  // Mapbox v3 exposes multiple lights; we pick the first object-like light we can understand.
  if (Array.isArray(value)) return value.find(isRecord);
  if (!isRecord(value)) return undefined;

  const lights = value.lights;
  if (Array.isArray(lights)) return lights.find(isRecord);

  const directional = value.directional;
  if (isRecord(directional)) return directional;

  const light = value.light;
  if (isRecord(light)) return light;

  return value;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object';
}

function isLightPosition(
  value: unknown,
): value is readonly [number, number, number] {
  if (!Array.isArray(value) || value.length !== 3) return false;
  return value.every((v) => typeof v === 'number' && Number.isFinite(v));
}

/**
 * Convert Mapbox light.position ([r, azimuthal, polar]) to a direction vector.
 *
 * We treat mapbox mercator axes as:
 * - x: east
 * - y: south (matches mercator increasing downward)
 * - z: up
 */
function lightPositionToDirection(
  position: readonly [number, number, number],
): [number, number, number] {
  const [, azimuthalDeg, polarDeg] = position;

  const az = (azimuthalDeg * Math.PI) / 180;
  const polar = (polarDeg * Math.PI) / 180;

  const sinPolar = Math.sin(polar);
  const cosPolar = Math.cos(polar);

  // azimuthal is clockwise from north; with y pointing south, north is -y.
  const x = Math.sin(az) * sinPolar;
  const y = -Math.cos(az) * sinPolar;
  const z = cosPolar;

  return normalize3([x, y, z]) as [number, number, number];
}

function normalize3(
  v: readonly [number, number, number],
): readonly [number, number, number] {
  const [x, y, z] = v;
  const len = Math.sqrt(x * x + y * y + z * z);
  if (!len) return [0, 0, 1];
  return [x / len, y / len, z / len];
}

function clamp01(value: number): number {
  return clamp(value, 0, 1);
}

function clamp(value: number, min: number, max: number): number {
  if (value < min) return min;
  if (value > max) return max;
  return value;
}
